import { useState, useCallback } from 'react';
import { RotateCw, RotateCcw, FlipHorizontal, FlipVertical, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const ImageRotateFlip = () => {
  useToolUsage('image-rotate-flip');
  const [file, setFile] = useState<File | null>(null);
  const [src, setSrc] = useState('');
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) { toast.error('Upload an image'); return; }
    setFile(f);
    setSrc(URL.createObjectURL(f));
    setRotation(0); setFlipH(false); setFlipV(false);
  }, []);

  const handleDownload = () => {
    if (!src) return;
    const img = new Image();
    img.onload = () => {
      const isRotated = rotation % 180 !== 0;
      const w = isRotated ? img.height : img.width;
      const h = isRotated ? img.width : img.height;
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.translate(w / 2, h / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      canvas.toBlob(blob => {
        if (!blob) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `edited_${file?.name || 'image.png'}`;
        a.click();
        toast.success('Downloaded!');
      }, 'image/png');
    };
    img.src = src;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <RotateCw className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Image Rotate & Flip</h1>
          <p className="text-muted-foreground">Rotate and flip images with one click</p>
        </div>
      </div>

      {!src ? (
        <Card>
          <CardContent className="pt-6">
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-primary transition-colors">
                <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">Drop an image here or click to upload</p>
              </div>
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            </label>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setRotation(r => (r - 90) % 360)}><RotateCcw className="h-4 w-4 mr-2" /> Rotate Left</Button>
            <Button variant="outline" onClick={() => setRotation(r => (r + 90) % 360)}><RotateCw className="h-4 w-4 mr-2" /> Rotate Right</Button>
            <Button variant="outline" onClick={() => setFlipH(f => !f)}><FlipHorizontal className="h-4 w-4 mr-2" /> Flip H</Button>
            <Button variant="outline" onClick={() => setFlipV(f => !f)}><FlipVertical className="h-4 w-4 mr-2" /> Flip V</Button>
          </div>

          <Card>
            <CardContent className="pt-6 flex justify-center">
              <div className="max-h-[60vh] overflow-hidden">
                <img
                  src={src} alt="Preview"
                  className="max-h-[60vh] transition-transform duration-300"
                  style={{
                    transform: `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`,
                  }}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button onClick={handleDownload}><Download className="h-4 w-4 mr-2" /> Download</Button>
            <Button variant="outline" onClick={() => { setSrc(''); setFile(null); }}>Clear</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageRotateFlip;
